import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { XMarkIcon } from '../icons';

interface ProjectModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (description: string) => void;
    initialDescription?: string;
    isGenerating?: boolean;
}

const ProjectModal: React.FC<ProjectModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    initialDescription = '',
    isGenerating = false
}) => {
    const { t } = useLanguage();
    const [description, setDescription] = useState(initialDescription);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-[#070910]/80 backdrop-blur-md animate-in fade-in duration-300">
            <div className="bg-white dark:bg-[#0D111C] rounded-3xl border border-slate-200 dark:border-white/10 shadow-2xl w-full max-w-xl overflow-hidden transform transition-all">
                <div className="px-6 py-4 border-b border-slate-100 dark:border-white/10 flex items-center justify-between bg-slate-50/50 dark:bg-[#070910]/40">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                        {t('labels.project_description')}
                    </h3>
                    <button
                        onClick={onClose}
                        className="p-1 hover:bg-slate-200 dark:hover:bg-white/10 rounded-full transition-colors"
                    >
                        <XMarkIcon className="w-6 h-6 text-slate-500 dark:text-slate-400" />
                    </button>
                </div>

                <div className="p-6 space-y-4">
                    <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                        {t('labels.enter_project_description_hint')}
                    </p>

                    <div className="relative">
                        <textarea
                            autoFocus
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder={t('labels.project_description')}
                            maxLength={120}
                            className="w-full h-20 px-4 py-3 text-sm bg-white dark:bg-[#161B2E] border border-slate-300 dark:border-white/10 text-gray-900 dark:text-white rounded-xl focus:outline-none focus:ring-1 focus:ring-orange-500/50 transition-all shadow-inner placeholder-slate-400 resize-none font-medium"
                        />
                        <div className="absolute bottom-3 right-3 text-[10px] text-slate-400">
                            {description.length}/120
                        </div>
                    </div>
                </div>

                <div className="px-6 py-4 bg-slate-50 dark:bg-[#070910]/40 flex gap-3 justify-end border-t dark:border-white/10">
                    <button
                        onClick={onClose}
                        className="px-6 py-2.5 text-sm font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-white/10 rounded-xl transition-all"
                    >
                        {t('common.cancel', 'İptal')}
                    </button>
                    <button
                        onClick={() => onConfirm(description)}
                        disabled={isGenerating}
                        className="px-8 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold text-sm rounded-xl shadow-lg shadow-orange-500/20 active:scale-[0.98] transition-all flex items-center gap-2 disabled:opacity-50"
                    >
                        {isGenerating ? (
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                        )}
                        {t('common.download')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ProjectModal;
